import { useEffect, useMemo, useState } from 'react';
import { Copy, ExternalLink, Search } from 'lucide-react';
import { fetchAdminReplyPlaybooks, type AdminReplyPlaybook } from '@/api/adminReplyPlaybooks';
import { tenantsApi, type Tenant } from '@/api/tenants';
import { AdminReplyPlaybookPageSharePanel } from '@/components/features/admin-reply-playbooks/AdminReplyPlaybookPageSharePanel';
import { AdminReplyPlaybookSharePanel } from '@/components/features/admin-reply-playbooks/AdminReplyPlaybookSharePanel';
import { MainLayout } from '@/components/layout/MainLayout';
import { TenantSelector } from '@/components/ui';
import { useTenantScope } from '@/hooks/useTenantScope';
import { DependencyStrip } from '@/features/monitoring/DependencyStrip';

const KH_EDITOR_URL = 'https://kb.med-integra.ru/app/kb-admin/ai-policies?schema_type=admin_reply_playbook';

export function AdminReplyPlaybooksPage() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const [items, setItems] = useState<AdminReplyPlaybook[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return;
    tenantsApi.list().then((data) => setTenants(data.tenants || [])).catch(() => setTenants([]));
  }, [isSuperAdmin]);

  const selectedTenantSlug = useMemo(() => {
    if (!isSuperAdmin) return undefined;
    return tenants.find((tenant) => tenant.id === tenantId)?.slug;
  }, [isSuperAdmin, tenantId, tenants]);

  useEffect(() => {
    if (isSuperAdmin && !selectedTenantSlug) {
      setItems([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    fetchAdminReplyPlaybooks(selectedTenantSlug)
      .then(setItems)
      .catch(() => setError('Не удалось загрузить подсказки из Knowledge Hub'))
      .finally(() => setLoading(false));
  }, [isSuperAdmin, selectedTenantSlug]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => {
      const payload = item.payload;
      return [
        item.title,
        payload.topic,
        payload.recommended_answer,
        ...(payload.trigger_phrases || []),
      ].some((value) => value.toLowerCase().includes(needle));
    });
  }, [items, query]);

  const selected = filtered.find((item) => item.id === selectedId) || filtered[0] || null;

  return (
    <MainLayout headerTitle="Подсказки админам" disableMainScroll>
      <div className="flex h-full min-h-0">
        <aside className="flex w-[320px] min-w-0 shrink-0 flex-col border-r border-shell-border bg-shell-surface">
          <div className="space-y-2 border-b border-shell-border p-3">
            {isSuperAdmin && (
              <TenantSelector
                value={selectedTenantId || ''}
                onChange={setSelectedTenant}
                showLabel={false}
                required
                testId="admin-reply-playbooks-tenant-selector"
              />
            )}
            <label className="flex min-w-0 items-center gap-2 rounded-lg border border-shell-border bg-shell-surface px-3">
              <Search className="h-4 w-4 shrink-0 text-shell-muted" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Поиск по теме или ответу"
                className="h-9 w-full min-w-0 bg-transparent text-sm text-shell-strong outline-none placeholder:text-shell-muted"
              />
            </label>
            <DependencyStrip edge="knowledge_hub" compact />
            {isSuperAdmin && selectedTenantSlug && !loading && !error && (
              <AdminReplyPlaybookPageSharePanel tenantSlug={selectedTenantSlug} totalItems={items.length} />
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {requireTenantSelection && <p className="p-3 text-sm text-shell-muted">Выберите клинику, чтобы загрузить подсказки.</p>}
            {loading && <p className="p-3 text-sm text-shell-muted">Загружаем подсказки...</p>}
            {error && <p className="p-3 text-sm text-shell-danger">{error}</p>}
            {!requireTenantSelection && !loading && !error && filtered.length === 0 && (
              <p className="p-3 text-sm text-shell-muted">Подсказок нет или поиск ничего не нашёл.</p>
            )}
            {filtered.map((item) => (
              <button
                key={`${item.tenant_id}-${item.id}`}
                type="button"
                onClick={() => setSelectedId(item.id)}
                className={`block w-full border-b border-shell-border px-3 py-3 text-left ${selected?.id === item.id ? 'bg-shell-primary-soft' : 'hover:bg-shell-hover'}`}
              >
                <p className="truncate text-sm font-semibold text-shell-strong">{item.title}</p>
                <p className="mt-1 truncate text-xs text-shell-muted">
                  {item.payload.topic}
                  {item.payload.needs_human_review ? ' · проверить перед отправкой' : ''}
                </p>
              </button>
            ))}
          </div>
        </aside>
        <section className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-shell-bg p-4">
          {!selected ? (
            <p className="text-sm text-shell-muted">Выберите подсказку слева. Ответы не уходят пациентам сами.</p>
          ) : (
            <article className="mx-auto max-w-3xl rounded-xl border border-shell-border bg-shell-surface p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-shell-muted">{selected.payload.topic}</p>
                  <h2 className="mt-1 text-base font-semibold text-shell-strong">{selected.title}</h2>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(selected.payload.recommended_answer)}
                    className="shell-button-secondary h-9"
                  >
                    <Copy className="h-4 w-4" />
                    Скопировать
                  </button>
                  <a href={KH_EDITOR_URL} target="_blank" rel="noreferrer" className="shell-button-secondary h-9">
                    Править в KH
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
              <p className="mt-4 whitespace-pre-wrap text-sm leading-5 text-shell-text">{selected.payload.recommended_answer}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(selected.payload.trigger_phrases || []).map((phrase) => (
                  <span key={phrase} className="shell-chip">{phrase}</span>
                ))}
              </div>
              {isSuperAdmin && selectedTenantSlug && (
                <AdminReplyPlaybookSharePanel playbookId={selected.id} tenantSlug={selectedTenantSlug} />
              )}
            </article>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
